import './JobsPage.scss'

import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import ApplyModal from "./modals/ApplyModal";
import JobPostingModal from "./modals/JobPostingModal";
import { useCurrentUser } from '../../UserProvider';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { Link } from 'react-router-dom';

export default function JobsPage() {
  const { currentUser } = useCurrentUser();

  const [search, setSearch]                   = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [jobs, setJobs]                       = useState([]);
  const [selectedJob, setSelectedJob]         = useState(null);
  const [jobToEdit, setJobToEdit]             = useState(null);
  const [page, setPage]                       = useState(1);
  const [hasMore, setHasMore]                 = useState(true);
  const perPage = 10;

  // Update debouncedSearch after typing delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      if (search.trim()) {
        setJobs([]);
      }
      setHasMore(true);
    }, 800);

    return () => clearTimeout(handler);
  }, [search]);

  // Fetch jobs whenever debouncedSearch or page changes
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const payload = {
          query: debouncedSearch,
          per_page: perPage,
          page: page,
        };

        const { data } = await api.post(`/job_postings/search`, payload);

        if (page === 1) {
          setJobs(data);
        } else {
          setJobs(prev => [...prev, ...data]);
        }

        // If fewer than perPage jobs came back, no more pages
        if (data.length < perPage) {
          setHasMore(false);
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
      }
    };

    fetchJobs();
  }, [debouncedSearch, page]);

  const downloadAll = async () => {
    const payload = {
      query:    debouncedSearch,
      per_page: 500,
      page:     1,
    };

    const { data } = await api.post(`/job_postings/search`, payload);

    if (!Array.isArray(data) || data.length === 0) {
      alert("No data found.");
      return;
    }

    // Get headers from first item
    const headers = Object.keys(data[0]);

    // Map each row to CSV format
    const csvRows = [
      headers.join(","), // Header row
      ...data.map(item =>
        headers
          .map(header => {
            let val = item[header];
            if (val === null || val === undefined) val = "";
            // Escape quotes and wrap in quotes if needed
            val = String(val).replace(/"/g, '""');
            return `"${val}"`;
          })
          .join(",")
      ),
    ];

    const csvString = csvRows.join("\n");

    // Create a blob and download
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute("download", "job_postings.csv");
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log("CSV downloaded!");

    console.log(data);
  };

  const reIndex = async () => {
    await api.post(`/job_postings/embed`);
    alert('Done indexing job postings');
  };

  return (
    <div className='__jobs'>
      <div className='content'>
        <div className='header'>
          <div className='primary'>
            Find all job opportunities in the music industry in one place, at your finger tips.
          </div>
          <div className='secondary'>
            All job opportunities in one place. Find the right one for you.
          </div>
        </div>

        {currentUser?.is_admin &&
          <div className='admin-buttons'>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={reIndex}>
              Index Job Postings
            </button>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={() => setJobToEdit(undefined)}>
              Add New Job Posting
            </button>
            <button className='add-new ppy-btn ppy-btn-primary' onClick={() => downloadAll()}>
              Download All
            </button>
          </div>
        }

        <input
          className='search'
          type="text"
          placeholder="Filter jobs by typing here..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className='bottom-section'>
          <div className='search-results'>
            {jobs.map((job) => (
              <div key={job.id} className='job-card'>
                <div className='job-header'>
                  <Link to={`/jobs/${job.id}`} className='job-title'>
                    {job.title}
                  </Link>
                  {currentUser?.is_admin &&
                    <button className='edit ppy-btn ppy-btn-primary' onClick={() => setJobToEdit(job)}>
                      <FontAwesomeIcon icon={faPencil} />
                    </button>
                  }
                </div>

                {job.tags && job.tags.trim() !== '' && (
                  <div className='tags'>
                    {job.tags.split(',').map((tag, idx) => (
                      <div key={idx} className='tag'>
                        {tag.trim()}
                      </div>
                    ))}
                  </div>
                )}

                {!!job.budget && job.budget !== 0 && (
                  <div className='budget'>Budget: ${job.budget}</div>
                )}

                <div className='body'>{job.description}</div>
                <div className='location'><FontAwesomeIcon icon={faLocationDot} /> {job.location}</div>
                <div className='apply'>
                  <button className="ppy-btn ppy-btn-primary" onClick={() => setSelectedJob(job)}>
                    Apply Now
                  </button>
                </div>
                <div className="dates">
                  <div>Posted {new Date(job.created_at).toLocaleDateString('en-US')}</div>
                  { job.expires_at &&
                    <div className="expires-at">
                      <div>Expires {new Date(job.expires_at).toLocaleDateString('en-US')}</div>
                      {new Date(job.expires_at) < new Date() && (
                        <div className="expired">Expired</div>
                      )}
                    </div>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
        {hasMore && jobs.length > 0 && (
          <div className="load-more">
            <button
              className="ppy-btn ppy-btn-primary"
              onClick={() => setPage(prev => prev + 1)}
            >
              Load More
            </button>
          </div>
        )}
      </div>

      <ApplyModal
        show={!!selectedJob}
        handleClose={() => setSelectedJob(null)}  
        job={selectedJob}
      />

      <JobPostingModal
        show={jobToEdit !== null}
        handleClose={() => setJobToEdit(null)}
        job={jobToEdit}
        setJobs={setJobs}
      />
    </div>
  );
}
